import moment from "moment";
import React from "react";

import { NotificationType } from "../../Enums/NotificationType";
import { INotification } from "../../Models/Notification";
import { ErrorExclamation } from "./NotificationsStyled";

export function notificationHasError (type: NotificationType) : boolean{
    return type === NotificationType.MatchReportError
    || type === NotificationType.MobilyticsReportError;
}

function getPercentComplete (type: NotificationType): 0 | 25 | 50 | 75 | 100 {
    switch (type){
        case NotificationType.MatchReportRunning:
        case NotificationType.MobilyticsReportRunning: {
            return 50;
        }
        case NotificationType.MatchReportCaching:
        case NotificationType.MobilyticsReportCaching: {
            return 75;
        }
        case NotificationType.MatchReportComplete:
        case NotificationType.MobilyticsReportComplete:
        case NotificationType.MatchReportError:
        case NotificationType.MobilyticsReportError: {
            return 100;
        }

        default: {
            return 0;
        }
    }
}

export function getFilteredNotifications (notifications: INotification[], term: string, filterDates: [Date, Date]):INotification[]{

    const modifiedTerm: string = term.trim().toLowerCase();

    if (modifiedTerm.length === 0){
        /** only filter on dates */
        return notifications.filter((n: INotification) => {
            return n.createdDate >= filterDates[0]
             && n.createdDate <= filterDates[1]
             && n.isActive;
        });
    }
    /** filter on multiple fields */
    return notifications.filter((n: INotification) => {
        return n.title.trim().toLowerCase().includes(modifiedTerm)
        || n.description.trim().toLowerCase().includes(modifiedTerm)
        && (
            n.createdDate >= filterDates[0]
         && n.createdDate <= filterDates[1]
        )
        && n.isActive;
    });
}

export function getInitialNotificationFilterDates (): [Date, Date] {
    const from = new Date();
    const to = new Date();
    // the beginning of the day
    from.setHours(0, 0, 0, 0);
    // 30 days back
    from.setDate(from.getDate() - 30);
    // the end of the day
    to.setHours(23, 59, 59, 0);
    return [
        from,
        to
    ];
};

export function getFixedNotificationDates(start: Date, end: Date): [Date, Date] {
    /** new date objects - so we're not altering the passed in data */
    const s = new Date(start);
    const e = new Date (end);
    /** fix the dates to encompass the beginning and the end of the days */
    s.setHours(0, 0, 0, 0);// beginning of the day
    e.setHours(23, 59, 59, 0);// the end of the day
    /** return the fixed dates */
    return [s, e];
}

export function getNotificationDateTimeValue (dateTime: Date): string | Date {
    const numMinutes: number = moment().diff(moment(dateTime), "minutes");
    /** the notification has appeared within the minute */
    if (numMinutes === 0){
        return "Just now";
    }
    const numHours: number = moment().diff(moment(dateTime), "hours");
    /** the notification has appeared within the hour */
    if (numHours === 0){
        const numMinutes: number = moment().diff(moment(dateTime), "minutes");
        return `${numMinutes} minutes ago`;
    }
    /** 24 hours in a day */
    if (numHours < 24){
        return `${numHours} hours ago`;
    }
    return dateTime.toLocaleDateString();
};

interface IconProps {
    height?: string;
    width?: string;
    fill?: string;
    opacity?: string;
}

const getMatchIcon = (props?: IconProps): JSX.Element => {
    const h : string = props?.height !== undefined ? props.height : "24";
    const w : string = props?.width !== undefined ? props.width : "19";
    const f : string = props?.fill !== undefined ? props.fill : "#2E3D49";
    const o : string = props?.opacity !== undefined ? props.opacity : ".65";
    return (
        <svg width={w} height={h} viewBox="0 0 19 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g opacity={o}>
                <path fillRule="evenodd" clipRule="evenodd" d="M11.0388 1.58421L12.088 2.65071H10.9814L9.67814 1.32535L10.9814 0H12.088L11.0388 1.06649H17.8201V1.58421H11.0388ZM7.32397 12.3838H4.78407V10.0477H7.32397V12.3838ZM6.0082 19.7747H2.10648V14.9811H6.0082V19.7747ZM3.4838 12.3836H0.942975V10.0477H3.4838V12.3836ZM3.47755 8.53669H0.939504V6.20156H3.47755V8.53669ZM3.47663 4.64158H0.938578V2.30668H3.47663V4.64158ZM7.32327 8.49433H4.78661V6.1559H7.32327V8.49433ZM7.32073 4.64535H4.788V2.30339H7.32073V4.64535ZM0 0.378662H8.26301V20.3251L0 20.274V0.378662ZM17.1288 15.6809H14.5887V13.3449H17.1288V15.6809ZM15.8128 23.0718H11.9111V18.2782H15.8128V23.0718ZM13.2882 15.6809H10.7474V13.3451H13.2882V15.6809ZM13.2822 11.8338H10.7441V9.4987H13.2822V11.8338ZM13.2813 7.93873H10.743V5.60383H13.2813V7.93873ZM17.1279 11.7915H14.5912V9.45305H17.1279V11.7915ZM17.1254 7.94249H14.5926V5.60053H17.1254V7.94249ZM9.80439 23.5712V3.67581H18.0676V23.6222L9.80439 23.5712ZM5.96164 21.2623L7.01083 22.3288H0.229507V22.8465H7.01083L5.96164 23.913H7.06822L8.37149 22.5877L7.06822 21.2623H5.96164Z" fill={f}/>
            </g>
        </svg>
    );
};

const getMobilyticsIcon = (props?: IconProps): JSX.Element => {
    const h : string = props?.height !== undefined ? props.height : "24";
    const w : string = props?.width !== undefined ? props.width : "13";
    const f : string = props?.fill !== undefined ? props.fill : "#2E3D49";
    const o : string = props?.opacity !== undefined ? props.opacity : ".65";
    return (
        <svg width={w} height={h} viewBox="0 0 13 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g opacity={o}>
                <path fillRule="evenodd" clipRule="evenodd" d="M10.6882 19.0746H1.43464V4.72795H10.6882V19.0746ZM5.62408 22.1127C5.9969 22.1306 6.37233 22.1313 6.74515 22.1115C7.16212 22.0893 7.45712 21.7451 7.45044 21.3279C7.444 20.9141 7.1299 20.5935 6.71006 20.5785C6.59195 20.5742 6.47349 20.5753 6.35501 20.5763C6.30206 20.5768 6.24912 20.5773 6.19619 20.5773C6.14187 20.5772 6.08751 20.5768 6.03313 20.5763C5.9083 20.5753 5.78338 20.5743 5.65869 20.5778C5.2379 20.5897 4.91951 20.9036 4.90495 21.316C4.88991 21.7373 5.19709 22.0922 5.62408 22.1127ZM10.005 0.0133285C10.0237 0.0234633 10.0425 0.0336185 10.0626 0.037711C11.498 0.327227 12.337 1.35473 12.3332 2.81568C12.3313 3.5192 12.3317 4.22284 12.3321 4.92651C12.3323 5.22287 12.3325 5.51924 12.3325 5.81561V6.05429L12.3279 16.4251C12.3365 18.05 12.3363 19.6749 12.332 21.2998C12.3282 22.7813 11.1715 23.9828 9.71369 23.9911C7.35603 24.004 4.9979 24.0019 2.63977 23.9911C1.42276 23.9856 0.364222 23.1111 0.0928455 21.9237C0.0691134 21.8201 0.0487483 21.7159 0.0283639 21.6117C0.019022 21.5639 0.009676 21.5161 0 21.4683V2.53117C0.00477039 2.51224 0.0100452 2.49336 0.0153216 2.47447C0.0266767 2.43383 0.0380387 2.39315 0.044394 2.35193C0.218628 1.22943 0.849452 0.490482 1.91801 0.122203C2.02285 0.0862196 2.13151 0.0609964 2.24013 0.0357801C2.29002 0.0241992 2.3399 0.0126197 2.3894 0H9.9791C9.98779 0.00404885 9.99637 0.00868654 10.005 0.0133285ZM7.96303 10.3108C7.97759 9.36205 7.18733 8.58325 6.19276 8.56654C5.19628 8.54983 4.38764 9.3105 4.37165 10.279C4.35661 11.1977 5.15881 11.9904 6.12282 12.0092C7.11835 12.0288 7.94823 11.2624 7.96303 10.3108ZM6.12933 16.5266C6.10574 16.4886 6.08561 16.4561 6.06773 16.4226C5.82988 15.9754 5.59176 15.5283 5.35364 15.0813C4.73393 13.9178 4.11425 12.7544 3.49956 11.5887C2.58185 9.84894 3.69026 7.76505 5.69491 7.44761C5.72674 7.44249 5.75814 7.43351 5.78947 7.42456C5.80489 7.42015 5.8203 7.41574 5.83573 7.41181H6.49854C6.58974 7.43726 6.68232 7.45971 6.7749 7.48215C6.97402 7.53044 7.17316 7.57872 7.35873 7.65693C8.94976 8.32785 9.61543 10.0943 8.834 11.5877C8.2322 12.7383 7.62037 13.8841 7.00848 15.0301C6.75521 15.5044 6.50194 15.9787 6.24936 16.4534C6.23574 16.4787 6.22029 16.5031 6.20193 16.5321C6.19133 16.5488 6.17977 16.5671 6.16702 16.588C6.15351 16.5656 6.141 16.5455 6.12933 16.5266Z" fill={f}/>
            </g>
        </svg>
    );
};

const getScoutIcon = (props?: IconProps): JSX.Element => {
    const h : string = props?.height !== undefined ? props.height : "24";
    const w : string = props?.width !== undefined ? props.width : "24";
    const f : string = props?.fill !== undefined ? props.fill : "#2E3D49";
    const o : string = props?.opacity !== undefined ? props.opacity : ".65";
    return (
        <svg width={w} height={h} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g opacity={o}>
                <path fillRule="evenodd" clipRule="evenodd" d="M20.3667 12.5615C19.9642 16.4343 16.8208 19.4985 12.85 19.8871V17.2101H11.151V19.8871C7.17903 19.4985 4.03562 16.4343 3.63402 12.5615H6.37375V10.8878H3.63402C4.03562 7.01469 7.17903 3.95056 11.151 3.56221V6.23914H12.85V3.56221C16.8208 3.95056 19.9642 7.01469 20.3667 10.8878H17.6261V12.5615H20.3667ZM22.0749 10.8879C21.6635 6.07418 17.7781 2.2793 12.8501 1.8791V0H11.1511V1.8791C6.22331 2.2793 2.33767 6.07418 1.92514 10.8879H0V12.5616H1.92514C2.33767 17.3755 6.22331 21.1701 11.1511 21.5694V23.4483H12.8501V21.5694C17.7781 21.1701 21.6633 17.3755 22.0749 12.5616H24V10.8879H22.0749Z" fill={f}/>
            </g>
        </svg>

    );
};

export function getNotificationIcon(n: INotification): JSX.Element {
    const opacity: string = n.isRead
        ? ".65"
        : "1";
    const fill: string = n.isRead
        ? "#c4c4c4"
        : "#fff";
    switch (n.notificationType) {
        case NotificationType.MobilyticsReportPending:
        case NotificationType.MobilyticsReportRunning: {
            return getMobilyticsIcon({fill: fill, opacity: opacity});
        }
        case NotificationType.MatchReportPending:
        case NotificationType.MatchReportRunning: {
            return getMatchIcon({fill: fill, opacity: opacity});
        }
        case NotificationType.MatchReportCaching: {
            return getMatchIcon({fill: fill, opacity: opacity});
        }
        case NotificationType.MobilyticsReportCaching: {
            return getMobilyticsIcon({fill: fill, opacity: opacity});
        }
        case NotificationType.MobilyticsReportComplete: {
            return getMobilyticsIcon({fill: n.isRead ? "#c4c4c4" : "#2b8b49", opacity: opacity});
        }
        case NotificationType.MatchReportComplete: {
            return getMatchIcon({fill: n.isRead ? "#c4c4c4" : "#2b8b49", opacity: opacity});
        }
        case NotificationType.MatchReportError: {
            return (
                <>
                    <ErrorExclamation isRead={n.isRead}>!</ErrorExclamation>
                    {getMatchIcon()}
                </>
            );
        }
        case NotificationType.MobilyticsReportError: {
            return (
                <>
                    <ErrorExclamation isRead={n.isRead}>!</ErrorExclamation>
                    {getMobilyticsIcon()}
                </>
            );
        }
        default: {
            return getScoutIcon();
        }
    }
}

export function getBaseUrlFromNotificationType(type: NotificationType):string {
    switch (type){
        case NotificationType.MatchReportComplete: {
            return `retailMatch`;
        }
        case NotificationType.MobilyticsReportComplete: {
            return `gps/insight`;
        }
    }
    return null;
}

export function getNotificationDescriptionForType(type: NotificationType): string {
    let msg = "";
    switch (type){
        case NotificationType.MatchReportPending:
        case NotificationType.MobilyticsReportPending: {
            msg = "This report is pending, and can be viewed once it completes.";
            break;
        }
        case NotificationType.MatchReportRunning:
        case NotificationType.MobilyticsReportRunning: {
            msg = "This report is running, and can be viewed once it completes.";
            break;
        }
        case NotificationType.MatchReportCaching:
        case NotificationType.MobilyticsReportCaching: {
            msg = "This report is caching, and can be viewed once it completes.";
            break;
        }
        case NotificationType.MatchReportError:
        case NotificationType.MobilyticsReportError: {
            msg = "This report has errored and cannot be viewed.";
            break;
        }
    }

    return msg;
}

export function getNotificationCursor(type: NotificationType): string {
    let cursor = "not-allowed";
    switch (type) {
        case NotificationType.MatchReportComplete:
        case NotificationType.MobilyticsReportComplete: {
            cursor = "pointer";
        }
    }
    return cursor;
}